/**
 * App 发布物「本地存储」前端封装（web-system 站点自带通道，不经后端 Hq.Api）。
 * - 上传/删除走同源 HTTP：dev = vite 插件（vite-plugin-local-upload.ts），
 *   生产 = upload-server.mjs（可直接与构建产物同进程跑，Windows Server 无需 nginx/IIS 反代）。
 * - 文件落站点根 apps 目录（web-system/apps），下载走 /apps/<文件名>（与站点同源）。
 * - 落库/二维码用的绝对地址由 buildAppsUrl 拼接（口径同参考项目 hsmsx_webf apps-upload.ts）。
 * 后端只接收「上报」的元数据（见 api-sdk appVersion.release.record），不再读写文件。
 */

export type AppsUploadMode = 'http' | 'unavailable';

const HEALTHZ_URL = '/local-upload/apps/healthz';
const UPLOAD_URL = '/local-upload/apps/upload';
const DELETE_URL = '/local-upload/apps/delete';

export interface AppsUploadResult {
  fileName: string;
  /** 站点内相对路径，如 /apps/xxx.apk（已 encodeURIComponent）。 */
  path: string;
  size: number;
}

/** 探活：判断本地上传通道是否可用（dev 插件 / prod upload-server 均返回同结构）。 */
export async function probeAppsUploadService(): Promise<AppsUploadMode> {
  try {
    const resp = await fetch(HEALTHZ_URL, { method: 'GET', cache: 'no-store' });
    if (!resp.ok) return 'unavailable';
    const r = (await resp.json()) as { success?: boolean; data?: { service?: string } };
    return r?.success && r?.data?.service ? 'http' : 'unavailable';
  } catch {
    return 'unavailable';
  }
}

/**
 * 上传单个文件到站点本地 apps 目录（原始请求体流式，非 multipart）。
 * @param body 文件内容（File / Blob）
 * @param name 期望文件名（服务端会净化 + 防重名，返回实际 fileName）
 */
export async function uploadAppFile(body: Blob, name: string): Promise<AppsUploadResult> {
  const resp = await fetch(`${UPLOAD_URL}?name=${encodeURIComponent(name)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    body
  });
  // 静态托管没接上通道时会返回 HTML（SPA 回退），不能直接 resp.json() 抛 SyntaxError
  const text = await resp.text();
  interface UploadEnvelope {
    success?: boolean;
    message?: string;
    data?: AppsUploadResult;
  }
  let r: UploadEnvelope | null = null;
  try {
    r = JSON.parse(text) as UploadEnvelope;
  } catch {
    r = null;
  }
  if (!resp.ok || !r?.success || !r.data) {
    throw new Error(
      r?.message ||
        (resp.status === 404
          ? '本地上传通道不可用（/local-upload/apps/upload 404）：请用 start-system 启动，或以 node upload-server.mjs --port 5010 --dist dist 启动站点'
          : `上传失败（HTTP ${resp.status}）`)
    );
  }
  return r.data;
}

/** 删除站点本地 apps 目录中的文件（尽力而为，不存在视为成功）。 */
export async function deleteAppFile(fileName: string): Promise<void> {
  try {
    await fetch(`${DELETE_URL}?name=${encodeURIComponent(fileName)}`, { method: 'POST' });
  } catch {
    /* 删除失败不阻断主流程（元数据软删以后端为准） */
  }
}

/** 归一化下载域名：补 https、去尾斜杠；空值返回空串（表示跟随访问域名）。 */
export function normalizeDownloadHost(value?: string | null): string {
  const raw = String(value ?? '').trim().replace(/\/+$/, '');
  if (!raw) return '';
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

/**
 * 生成落库/二维码的绝对下载地址。
 * 优先级：App 级 downloadHost（版本页配置）> 当前访问 system 的 origin。
 * 与后端旧 BuildUrl 口径一致，但改由前端拼接（文件本就归站点保管，前端最清楚自己的对外域名）。
 */
export function buildAppsUrl(fileName: string, downloadHost?: string | null): string {
  const base = normalizeDownloadHost(downloadHost) || window.location.origin.replace(/\/+$/, '');
  return `${base}/apps/${encodeURIComponent(fileName)}`;
}

/** 页面内展示/内嵌图标用的同源相对地址（不受落库绝对域名影响，避免污染 canvas）。 */
export function buildAppsUrlSameOrigin(fileName: string): string {
  return `/apps/${encodeURIComponent(fileName)}`;
}

/**
 * 落库地址是否指向本机回环（localhost / 127.0.0.1 / ::1）。
 * 未配置 App 级 downloadHost 时地址按当前 origin 兜底，管理员若用 localhost 打开页面，
 * 就会把手机不可用的地址写进库和二维码——调用方据此提示。
 */
export function isLoopbackUrl(url?: string | null): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?(\/|$)/i.test(String(url ?? '').trim());
}
